import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  VStack,
  Select,
  Spacer,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  Textarea,
  Switch,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { getAreaName } from "../../utils/areaNames";

const SurveyPageForm = ({ initialData, onSubmit, onCancel }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [position, setPosition] = useState(initialData?.position || "");
  const [area, setArea] = useState(initialData?.area || "");
  const [factor, setFactor] = useState(initialData?.factor || "");
  const [strength, setStrength] = useState(initialData?.strength || "");
  const [development, setDevelopment] = useState(initialData?.development || "");
  const [extraTeamOnly, setExtraTeamOnly] = useState(initialData?.extra_team_only || false);

  useEffect(() => {
    setName(initialData?.name || "");
    setPosition(initialData?.position || "");
    setArea(initialData?.area || "");
    setFactor(initialData?.factor || "");
    setStrength(initialData?.strength || "");
    setDevelopment(initialData?.development || "");
    setExtraTeamOnly(initialData?.extra_team_only || false);
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name, position, area, factor, strength, development, extra_team_only: extraTeamOnly });
  };

  const areaOptions = [
    { value: "input", label: getAreaName("input") },
    { value: "processes", label: getAreaName("processes") },
    { value: "climate_and_culture", label: getAreaName("climate_and_culture") },
  ];

  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.700");

  return (
    <Box mx="auto" p={6}>
      <Card bg={cardBg} shadow="none" borderRadius="lg">
        <CardHeader pb={0} textAlign="center">
          <FormLabel fontSize="xl" fontWeight="bold">
            {initialData ? "Edit Page" : "Create Page"}
          </FormLabel>
        </CardHeader>

        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5} align="stretch">
              {/* Page Name */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <FormControl isRequired>
                  <FormLabel>Page Name</FormLabel>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter page name" />
                </FormControl>
              </Box>

              {/* Position */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <strong>Configuration</strong>

                <Box pt={3}>
                  <FormControl>
                    <FormLabel>Position</FormLabel>
                    <NumberInput min={1} value={position} onChange={(value) => setPosition(value)}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </Box>

                <Box pt={3}>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">Exclusive to Multiple Teams</FormLabel>
                    <Switch isChecked={extraTeamOnly} onChange={(e) => setExtraTeamOnly(e.target.checked)} />
                    <Popover>
                      <PopoverTrigger>
                        <IconButton
                          aria-label="More info"
                          icon={<InfoIcon />}
                          size="sm"
                          ml={2}
                          variant="ghost"
                        />
                      </PopoverTrigger>
                      <PopoverContent>
                        <PopoverArrow />
                        <PopoverCloseButton />
                        <PopoverHeader>What is Exclusive to Multiple Teams?</PopoverHeader>
                        <PopoverBody pr={6}>
                          <p>This option marks the page as exclusive to respondents who belong to multiple teams.</p>
                          <p>So when the survey is mostly finished, a question will be presented to the respondent asking if they belong to other teams.</p>
                          <p>If they select any other team that they belong to, this page will appear at the end of the survey.</p>
                          <em>If unsure, don't activate it.</em>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </FormControl>
                </Box>
              </Box>


              {/* Factor */}
              <Box bg={sectionBg} p={4} borderRadius="md">
                <strong>Definition</strong>
                
                <Box pt={3}>
                  <FormControl isRequired>
                    <FormLabel>Area</FormLabel>
                    <Select value={area} onChange={(e) => setArea(e.target.value)} placeholder="Select an area">
                      {areaOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box pt={3}>
                  <FormControl>
                    <FormLabel>Factor</FormLabel>
                    <Input value={factor} onChange={(e) => setFactor(e.target.value)} placeholder="Enter factor" />
                  </FormControl>
                </Box>

              </Box>

              {/* Strength */}
              <Box bg={sectionBg} p={4} borderRadius="md">

                <strong>Results</strong>

                <Box pt={3}>
                  <FormControl>
                    <FormLabel>Strength</FormLabel>
                    <Textarea value={strength} onChange={(e) => setStrength(e.target.value)} placeholder="Enter strength description" size="md" />
                  </FormControl>
                </Box>

                <Box pt={3}>
                  <FormControl>
                    <FormLabel>Development</FormLabel>
                    <Textarea value={development} onChange={(e) => setDevelopment(e.target.value)} placeholder="Enter development description" size="md" />
                  </FormControl>
                </Box>

              </Box>


              {/* Buttons */}
              <Flex>
                <Button variant="outline" onClick={onCancel} size="sm">Cancel</Button>
                <Spacer />
                <Button type="submit" colorScheme="green" size="sm" variant="outline">
                  {initialData ? "Update Page" : "Create Page"}
                </Button>
              </Flex>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default SurveyPageForm;
